'use client';

import { Box, Container, Typography, useTheme } from '@mui/material';
import Image from 'next/image';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksAdditionalFeatures,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

export function AdditionalFeature({ props }: { props: ComponentBlocksAdditionalFeatures }) {
  const theme = useTheme();

  return (
    <CustomBackground
      backgroundOptions={
        (props?.AdditionalFeaturesBackground ?? undefined) as StrapiBackgroundOptions
      }
      displayOptions={
        {
          ...props.AdditionalFeaturesDisplayOptions,
          ContentsWidth:
            props.AdditionalFeaturesDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: props.AdditionalFeaturesDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: props.AdditionalFeaturesDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: props.AdditionalFeaturesDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error actually does accept data-* prop
        'data-component': 'AdditionalFeatures',
      }}
    >
      <Box>
        {props?.Title && (
          <Container maxWidth="md" sx={{ textAlign: 'center', mb: 1 }}>
            <ParsePillHeading title={props?.Title || ''} />
          </Container>
        )}

        <Box
          sx={{
            [theme.breakpoints.up('md')]: {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 6, // spacing between grid items
            },
          }}
        >
          {props?.AdditionalFeature?.map((feature, index) => (
            <Box
              key={index}
              sx={{
                my: { xs: 6, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              {/* Logo + Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {getImageUrl({ image: feature?.Logo, size: 'url' }) && (
                  <Image
                    src={getImageUrl({ image: feature?.Logo, size: 'url' })!}
                    width={feature?.Logo?.width || 70}
                    height={feature?.Logo?.height || 70}
                    alt={
                      feature?.Logo?.alternativeText ||
                      feature?.Header ||
                      'lend additional features logo'
                    }
                  />
                )}

                {feature?.Header && (
                  <Typography
                    variant="h3"
                    fontWeight={600}
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {feature.Header}
                  </Typography>
                )}
              </Box>

              {/* SubHeader */}
              {feature?.SubHeader && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 1.5, color: theme.palette.text.secondary }}
                >
                  {feature.SubHeader}
                </Typography>
              )}

              {/* Description */}
              {Array.isArray(feature?.Desc) && (
                <Box sx={{ mt: 1 }}>
                  <GlobalBlockRenderer
                    content={feature.Desc}
                    textColor={theme.palette.text.primary}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </CustomBackground>
  );
}
